import { OAuth2AuthCodePKCE } from '@bity/oauth2-auth-code-pkce'
import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport'
import { GUIDANCE_BANNER_STORAGE_KEYS, GuidanceBannerId } from 'components/core/guidance-banner/getGuidanceBanner'
import { FILTER_PICKER_STORAGE_KEY } from 'components/core/task/FilterPicker'
import { ModalContextShape } from 'components/no-ui/context/modal-provider'
import { COLLAPSED_SIDEBAR_NAV_STORAGE_KEY } from 'components/pages/sidebar/CollapsingSidebar'
import { Record as IRecord } from 'immutable'
import { RECENT_ITEMS_STORAGE_KEY, RecentItemsStore } from 'lib/constants/recent-items'
import { MutedDotsCacheKey as MUTED_DOTS_CACHE_KEY } from 'lib/constants/system'
import { User } from 'pbts/c1/api/user/v1/user'
import { createContext } from 'react'

export class AuthContextRecord extends IRecord({
    oauth: null as OAuth2AuthCodePKCE | null,
    transport: null as GrpcWebFetchTransport | null,
}) { }

export class UserContextRecord extends IRecord({
    roles: new Set<string>(),
    permissions: new Set<string>(),
    features: new Set<string>(),
    user: User.create(),
    status: 0,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    waitForReady: (): void => { },
}) { }

export class DashboardContextRecord extends IRecord<{
    request_tasks?: number
    review_tasks?: number
    revoke_tasks?: number
    enrollment_tasks?: number
    my_requests?: number
    my_expiring_access?: number
    conflicts?: number
}>({
    request_tasks: undefined,
    review_tasks: undefined,
    revoke_tasks: undefined,
    enrollment_tasks: undefined,
    my_requests: undefined,
    my_expiring_access: undefined,
    conflicts: undefined,
}) { }

type DisplayModeContextShape = 'drawer' | 'dialog' | 'default'

type ScrollContainerContextShape = {
    ref?: React.RefObject<HTMLDivElement>
}

export class AdminModeContextRecord extends IRecord({
    isOnAdminPath: false,
    hasAdminRole: false,
}) { }

export type LocalStorageStore = {
    [COLLAPSED_SIDEBAR_NAV_STORAGE_KEY]?: boolean
    [GUIDANCE_BANNER_STORAGE_KEYS]?: Record<GuidanceBannerId, boolean>
    [FILTER_PICKER_STORAGE_KEY]?: Record<string, string[]>
    [RECENT_ITEMS_STORAGE_KEY]?: RecentItemsStore
}

export type SessionStorageStore = {
    [MUTED_DOTS_CACHE_KEY]?: Record<string, boolean>
}

export class LocalStorageContextRecord extends IRecord<
    { store: LocalStorageStore, setStore: React.Dispatch<React.SetStateAction<LocalStorageStore>> }
>({
    store: {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setStore: (): void => { },
}) { }

export class SessionStorageContextRecord extends IRecord<
    { store: SessionStorageStore, setStore: React.Dispatch<React.SetStateAction<SessionStorageStore>> }
>({
    store: {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setStore: (): void => { },
}) { }

export const AuthContext = createContext<AuthContextRecord>(new AuthContextRecord())
export const UserContext = createContext<UserContextRecord>(new UserContextRecord())
export const DashboardContext = createContext<DashboardContextRecord>(new DashboardContextRecord())
export const ModalContext = createContext<ModalContextShape | undefined>(undefined)
export const DisplayModeContext = createContext<DisplayModeContextShape>('default')
export const ScrollContainerContext = createContext<ScrollContainerContextShape>({ ref: undefined })
export const AdminModeContext = createContext<AdminModeContextRecord>(new AdminModeContextRecord())
export const LocalStorageContext = createContext<LocalStorageContextRecord>(new LocalStorageContextRecord())
export const SessionStorageContext = createContext<SessionStorageContextRecord>(new SessionStorageContextRecord())
